import React, { memo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useUser } from 'Context/User';
import Uppy from '@uppy/core';
import Transloadit from '@uppy/transloadit';

import { AddExternalAtmosphericLogModal } from 'Components/RocketDry';

import { useRocketDryFunctions } from 'Context/RocketDry';

import { numberInputRegex } from 'Containers/RocketDry/helpers';

import { createExternalAtmosphericLog, setExternalAtmosphericLogCreated } from 'Containers/RocketDry/actions';
import { createExternalAtmosphericLogErrorsSelector } from 'Containers/RocketDry/selectors';
import { areEqual } from 'Utils/equalityChecks';

function AddExternalAtmosphericLogModalContainer() {
  const dispatch = useDispatch();

  const { project, isAddExternalAtmosphericLogModalOpen, setIsAddExternalAtmosphericLogModalOpen }: any =
    useRocketDryFunctions();

  const { id: userId } = useUser();

  const createExternalAtmosphericLogErrors = useSelector(createExternalAtmosphericLogErrorsSelector, areEqual);

  const [relativeHumidity, setRelativeHumidity] = useState('');
  const [temperature, setTemperature] = useState('');
  const [pressure, setPressure] = useState('');
  const [windSpeed, setWindSpeed] = useState('');
  const [photo, setPhoto] = useState<File>();
  const [isProcessing, setIsProcessing] = useState(false);

  const onChangeRelativeHumidity = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setRelativeHumidity(filtered);
  }, []);

  const onChangeTemperature = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setTemperature(filtered);
  }, []);

  const onChangePressure = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setPressure(filtered);
  }, []);

  const onChangeWindSpeed = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setWindSpeed(filtered);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsAddExternalAtmosphericLogModalOpen(false);
    setRelativeHumidity('');
    setTemperature('');
    setPressure('');
    setWindSpeed('');
    setPhoto(null);
    setIsProcessing(false);
  }, []);

  const onSaveExternalButtonClick = useCallback(async () => {
    if (project?.id) {
      setIsProcessing(true);
      const response: any = await dispatch(
        createExternalAtmosphericLog(project.id, relativeHumidity, temperature, pressure, windSpeed)
      );
      if (photo && response?.id) {
        const uppy = new Uppy().use(Transloadit, {
          assemblyOptions: {
            params: {
              auth: { key: import.meta.env.VITE_TRANSLOADIT_AUTH_ID },
              template_id: import.meta.env.VITE_TRANSLOADIT_ATMOSPHERICLOG_TEMPLATE_ID,
              fields: {
                user_id: userId,
                source: 'webapp',
                atmospheric_log_id: response.id,
              },
            },
          },
          waitForEncoding: true,
        });

        uppy.on('transloadit:complete', () => {
          // wait a bit before refetching, database needs to be synced first
          setTimeout(() => {
            dispatch(setExternalAtmosphericLogCreated(true));
            onCloseModal();
          }, 1000);
        });

        uppy.addFile({
          name: photo.name,
          type: photo.type,
          data: photo,
        });

        uppy.upload();
      } else {
        dispatch(setExternalAtmosphericLogCreated(true));
        onCloseModal();
      }
    }
  }, [project, relativeHumidity, temperature, pressure, windSpeed, userId, photo]);

  return (
    <AddExternalAtmosphericLogModal
      isOpen={isAddExternalAtmosphericLogModalOpen}
      closeModal={onCloseModal}
      relativeHumidity={relativeHumidity}
      temperature={temperature}
      pressure={pressure}
      windSpeed={windSpeed}
      photo={photo}
      formErrors={createExternalAtmosphericLogErrors}
      isProcessing={isProcessing}
      onSaveExternalButtonClick={onSaveExternalButtonClick}
      onChangeRelativeHumidity={onChangeRelativeHumidity}
      onChangePressure={onChangePressure}
      onChangeTemperature={onChangeTemperature}
      onChangeWindSpeed={onChangeWindSpeed}
      setPhoto={setPhoto}
    />
  );
}

const AddExternalAtmosphericLogModalMemo = memo(AddExternalAtmosphericLogModalContainer);

export { AddExternalAtmosphericLogModalMemo as AddExternalAtmosphericLogModal };
